import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useState } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GitFork } from 'lucide-react';
import { formatTime as formatTimeHHMMSS } from '@/lib/utils/date';
import type { ReportTask, ReportActivity, ReportAdjustment } from '../types';

interface SelectedEntity {
  id: number;
  name: string;
  type?: string;
  currency?: string;
  contactFace?: string;
  createdAt?: string;
}

interface ReportTasksTableProps {
  tasks: ReportTask[];
  selectedClient?: SelectedEntity | null;
  selectedContract?: SelectedEntity | null;
  refetch?: () => void;
  showBilledPrice?: boolean;
}

// Helper function to format seconds to hours and minutes
function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function toCurrency(value: number, locale: string, currency?: string): string {
  if (currency === 'UZS') {
    return `${value.toLocaleString(locale)} UZS`;
  }
  return `${value.toLocaleString(locale)} $`;
}

interface ActivityRowProps {
  activity: ReportActivity;
  index: number;
}

function ActivityRowComponent({ activity, index }: ActivityRowProps) {
  const hasMismatch =
    activity.userEnteredTimeInSeconds !== undefined &&
    activity.runTimeInSeconds !== undefined &&
    activity.userEnteredTimeInSeconds !== activity.runTimeInSeconds;

  const bgClass = index % 2 === 1 ? 'bg-[#F9F9F9]' : 'bg-[#E6F4FF]';
  const hoverClass = index % 2 === 1 ? 'hover:bg-[#F9F9F9]' : 'hover:bg-[#E6F4FF]';

  return (
    <TableRow className={`${bgClass} rounded-md ${hoverClass} border-none`}>
      <TableCell className={`${bgClass} rounded-l-md`}>
        <div
          className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all p-2 py-1 border bg-background shadow-xs ${
            hasMismatch ? 'border-red-600' : ''
          }`}
        >
          <div className="flex flex-col items-start gap-2 font-mono text-sm text-muted-foreground">
            {formatTimeHHMMSS(activity.userEnteredTimeInSeconds ?? activity.durationInSeconds ?? 0)}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div>{activity.description || '-'}</div>
      </TableCell>
      <TableCell className={`${bgClass} rounded-r-md`}>
        {/* Action buttons placeholder */}
      </TableCell>
    </TableRow>
  );
}

interface WorkBreakdownRowProps {
  index: number;
  name: string;
  isOriginal?: boolean;
  adjustmentPercent?: number | null;
  durationInSeconds: number;
  billableDurationInSeconds: number;
  billedPrice: number;
  activities: ReportActivity[];
  currency?: string;
  showBilledPrice: boolean;
}

// One breakdown line shown when a report row is expanded: either the original
// task or one of its adjustment children. Rendered as a row of the MAIN table
// so every cell lines up under the main column headers (no separate header).
// A light gray background marks it as a nested sub-row; it is itself expandable
// to reveal that item's activities. The empty cells keep alignment with the
// outer columns (action, #, date, createdBy, assignedTo, client, contract×3,
// description, matter, runTime, result, sendToClient×2, showTime, billedPrice).
function WorkBreakdownRow({
  index,
  name,
  isOriginal = false,
  adjustmentPercent,
  durationInSeconds,
  billableDurationInSeconds,
  billedPrice,
  activities,
  currency,
  showBilledPrice,
}: WorkBreakdownRowProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const activityList = activities ?? [];
  const hasActivities = activityList.length > 0;
  const leafCols = showBilledPrice ? 17 : 16;

  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        {/* Action */}
        <TableCell className="border-r text-center">
          {hasActivities ? (
            <Button
              size="sm"
              onClick={() => setOpen((o) => !o)}
              className="h-7 w-7 p-0 bg-[#0069D1]"
            >
              {open ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </Button>
          ) : null}
        </TableCell>
        {/* # */}
        <TableCell className="border-r text-center text-muted-foreground">↳ {index}</TableCell>
        {/* date */}
        <TableCell className="border-r" />
        {/* createdBy */}
        <TableCell className="border-r" />
        {/* assignedTo — owner + role + status badge (sits under "Assigned To") */}
        <TableCell className="border-r">
          <div className="flex items-center gap-2 whitespace-nowrap">
            {isOriginal ? (
              <FileText className="h-3.5 w-3.5 text-[#0069D1] shrink-0" />
            ) : (
              <GitFork className="h-3.5 w-3.5 text-blue-600 shrink-0" />
            )}
            <span className="font-medium">{name || '-'}</span>
            {isOriginal ? (
              <Badge variant="outline" className="text-xs">
                {t('adjustment.original')}
              </Badge>
            ) : adjustmentPercent != null ? (
              <Badge variant="outline" className="font-mono text-xs">
                {adjustmentPercent}% {t('adjustment.changed')}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                {t('adjustment.pending')}
              </Badge>
            )}
          </div>
        </TableCell>
        {/* client */}
        <TableCell className="border-r" />
        {/* contract group: contractNo / date / responsible */}
        <TableCell className="border-r" />
        <TableCell className="border-r" />
        <TableCell className="border-r" />
        {/* description */}
        <TableCell className="border-r" />
        {/* matter */}
        <TableCell className="border-r" />
        {/* runTime → total duration */}
        <TableCell className="border-r">{formatTime(durationInSeconds)}</TableCell>
        {/* task result */}
        <TableCell className="border-r" />
        {/* sendToClient group: date / time */}
        <TableCell className="border-r" />
        <TableCell className="border-r" />
        {/* showTime → billed duration */}
        <TableCell className="border-r">{formatTime(billableDurationInSeconds)}</TableCell>
        {/* billedPrice */}
        {showBilledPrice && (
          <TableCell className="border-r">{toCurrency(billedPrice, 'uz-UZ', currency)}</TableCell>
        )}
      </TableRow>
      {open && hasActivities ? (
        <TableRow className="bg-muted/50">
          <TableCell colSpan={leafCols} className="p-0">
            <div className="px-3 py-2">
              <Table>
                <TableBody>
                  {activityList.map((activity, i) => (
                    <ActivityRowComponent key={activity.id} activity={activity} index={i} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

export function ReportTasksTable({ tasks, selectedClient, selectedContract, refetch, showBilledPrice = false }: ReportTasksTableProps) {
  const { t } = useTranslation();
  const [expandedTasks, setExpandedTasks] = useState<Record<number, boolean>>({});

  const toggleTask = (taskId: number) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const totalDuration = tasks.reduce(
    (acc, task) => acc + (task.totalDurationInSeconds ?? task.durationInSeconds ?? 0),
    0,
  );
  const totalBilled = tasks.reduce(
    (acc, task) => acc + (task.totalBillableDurationInSeconds ?? task.billableDurationInSeconds ?? 0),
    0,
  );
  // Column total for "Billed Price" = sum of each row's rolled-up billed price
  // (original + its adjustments), mirroring the "Show Time" column total.
  const totalBilledPrice = tasks.reduce(
    (acc, task) => acc + (task.totalBilledPrice ?? task.billedPrice ?? 0),
    0,
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('Action')}</TableHead>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('#')}</TableHead>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('date')}</TableHead>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('createdBy')}</TableHead>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('assignedTo')}</TableHead>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('client')}</TableHead>
          <TableHead className="font-bold border-r" colSpan={3}>{t('contract')}</TableHead>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('description')}</TableHead>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('matter')}</TableHead>
          <TableHead className="font-bold border-r">{t('runTime')}</TableHead>
          <TableHead className="font-bold border-r" rowSpan={2}>{t('taskResultColumn')}</TableHead>
          <TableHead className="font-bold border-r" colSpan={2}>{t('sendToClient')}</TableHead>
          <TableHead className="font-bold border-r">{t('showTime')}</TableHead>
          {showBilledPrice && <TableHead className="font-bold border-r">{t('billedPrice')}</TableHead>}
        </TableRow>
        <TableRow>
          <TableHead className="border-r font-bold">{t('contractNo')}</TableHead>
          <TableHead className="border-r font-bold">{t('date')}</TableHead>
          <TableHead className="border-r font-bold">{t('responsible')}</TableHead>
          <TableHead className="border-r font-bold">{formatTime(totalDuration)}</TableHead>
          <TableHead className="border-r font-bold">DATE</TableHead>
          <TableHead className="border-r font-bold">Time</TableHead>
          <TableHead className="border-r font-bold">{formatTime(totalBilled)}</TableHead>
          {showBilledPrice && (
            <TableHead className="border-r font-bold">
              {toCurrency(totalBilledPrice, 'uz-UZ', selectedContract?.currency)}
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {tasks.map((task, index) => (
          <>
            <TableRow key={task.id} className="hover:bg-muted/50">
              <TableCell className="border-r">
                <div className="flex gap-4">
                  <Button
                    size="sm"
                    onClick={() => toggleTask(task.id)}
                    className="h-8 w-8 p-0 bg-[#0069D1]"
                  >
                    {expandedTasks[task.id] ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </TableCell>
              <TableCell className="border-r">{index + 1}</TableCell>
              <TableCell className="border-r">
                {new Date(task.createdAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Tashkent',
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </TableCell>
              <TableCell className="border-r">{task.createdBy?.name || '-'}</TableCell>
              <TableCell className="border-r">{task.assignedStaff?.name || '-'}</TableCell>
              <TableCell className="border-r">{selectedClient?.name || '-'}</TableCell>
              <TableCell className="border-r">{selectedContract?.name || '-'}</TableCell>
              <TableCell className="border-r">
                {selectedContract?.createdAt && !isNaN(new Date(selectedContract.createdAt).getTime())
                  ? new Date(selectedContract.createdAt).toLocaleDateString()
                  : '-'}
              </TableCell>
              <TableCell className="border-r">{task.createdBy?.name || '-'}</TableCell>
              <TableCell className="border-r align-top whitespace-pre-wrap break-words min-w-[200px] max-w-[460px]">
                {task.description || '-'}
              </TableCell>
              <TableCell className="border-r align-top whitespace-pre-wrap break-words min-w-[200px] max-w-[460px]">
                <b>{task.matter?.name || '-'}</b>
              </TableCell>
              <TableCell className="border-r">{formatTime(task.totalDurationInSeconds ?? task.durationInSeconds ?? 0)}</TableCell>
              <TableCell className="border-r align-top whitespace-pre-wrap break-words min-w-[160px] max-w-[360px]">{task.result || '-'}</TableCell>
              <TableCell className="border-r">
                {task.endedAt ? new Date(task.endedAt).toLocaleDateString() : '-'}
              </TableCell>
              <TableCell className="border-r">
                {task.endedAt
                  ? new Date(task.endedAt).toLocaleString('en-GB', {
                      timeZone: 'Asia/Tashkent',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })
                  : '-'}
              </TableCell>
              <TableCell className="border-r">{formatTime(task.totalBillableDurationInSeconds ?? task.billableDurationInSeconds ?? 0)}</TableCell>
              {showBilledPrice && (
                <TableCell className="border-r">
                  {toCurrency(task?.totalBilledPrice ?? task?.billedPrice ?? 0, 'uz-UZ', selectedContract?.currency)}
                </TableCell>
              )}
            </TableRow>

            {/* Expanded breakdown — original task + each adjustment child rendered
                as rows of the MAIN table, so each value (owner, total duration,
                billed duration, billed price) lines up under the main column
                headers. A light gray background marks them as nested sub-rows. */}
            {expandedTasks[task.id] &&
              (((task.activities?.length ?? 0) > 0) ||
                ((task.subtasks?.length ?? 0) > 0)) && (
                <>
                  <WorkBreakdownRow
                    index={1}
                    isOriginal
                    name={task.assignedStaff?.name ?? '-'}
                    durationInSeconds={task.durationInSeconds ?? 0}
                    billableDurationInSeconds={task.billableDurationInSeconds ?? 0}
                    billedPrice={task.billedPrice ?? 0}
                    activities={task.activities ?? []}
                    currency={selectedContract?.currency}
                    showBilledPrice={showBilledPrice}
                  />
                  {(task.subtasks ?? []).map((adj: ReportAdjustment, i) => (
                    <WorkBreakdownRow
                      key={`adj-${adj.id}`}
                      index={i + 2}
                      name={adj.assignedStaff?.name ?? '-'}
                      adjustmentPercent={adj.adjustmentPercent}
                      durationInSeconds={adj.durationInSeconds ?? 0}
                      billableDurationInSeconds={adj.billableDurationInSeconds ?? 0}
                      billedPrice={adj.billedPrice ?? 0}
                      activities={adj.activities ?? []}
                      currency={selectedContract?.currency}
                      showBilledPrice={showBilledPrice}
                    />
                  ))}
                </>
              )}
          </>
        ))}
      </TableBody>
    </Table>
  );
}
